import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import classnames from 'classnames/bind';

import {
  CartDrawer,
  MobileMenu,
  SearchInput,
  Button,
  PartnersBanner,
  HeaderActions,
  TenantSelector
} from '@components';
import { CategoriesToolbar } from '../CategoriesToolbar';
import { CategoryService } from '@services';
import Logo from '@assets/images/logo-new.png';
import LogoMobile from '@assets/images/logo-new-mobile.svg';
import styles from './PageHeader.module.scss';
import { ROUTES, SEARCH_ENGINE } from '@constants';
import { isFeatureEnabled, FEATURES } from '@utils/featureFlags';
import { CartContext, CategoriesTreeContext, SearchContext } from 'utils';
import pick from 'lodash/pick';
import { TENANT_STORAGE_KEY } from '@constants/tenant';
import { breakpoints, Icon, useMediaQuery } from 'gd-design-library';
import Cookies from 'js-cookie';
import Image from 'next/image';

const CN = 'page-header';
const PAGE_HEADER_HEIGHT_CSS_VAR = '--page-header-height';
const menuButtonClassName = `${CN}--menu-button`;
const logoClassName = `${CN}--logo`;
const toolbarClassName = `${CN}--toolbar`;
const cartButtonClassName = `${CN}--cart-button`;
const badgeClassName = `${cartButtonClassName}--badge`;
const cx = classnames.bind(styles);

export const PageHeader: React.FC = () => {
  const { query, push, pathname, isReady } = useRouter();
  const [ isDrawerOpen, setIsDrawerOpen ] = useState(false);
  const { searchTerm, setSearchTerm } = useContext(SearchContext);
  const { categories, setCategories } = useContext(CategoriesTreeContext);
  const { cart, isCartDrawerOpen, toggleCartDrawer } = useContext(CartContext);
  const isLargeScreen = useMediaQuery(`(min-width: ${breakpoints.xl})`);
  const isDesktop = useMediaQuery(`(min-width: ${breakpoints.lg})`);
  const isMobileOrTablet = useMediaQuery(`(max-width: ${breakpoints.md})`);
  const isPLP = pathname.startsWith('/catalog') || pathname.startsWith('/search');

  const handleDrawerToggle = (): void => setIsDrawerOpen(isOpen => !isOpen);
  const handleCategoryClick = (categoryKey: string): Promise<boolean> => push(`${ROUTES.catalog}/${categoryKey}`);

  const isConversationalSearchEnabled = useMemo(() => isFeatureEnabled(FEATURES.SMART_SEARCH), []);

  const handleSearch = (newSearchTerm: string = searchTerm): void => {
    if (newSearchTerm) {
      const queryParams = pick(query, 'sortBy');

      if (isConversationalSearchEnabled) {
        queryParams['conversational-search'] = 'true';
      }

      push({
        pathname: `${ROUTES.search}/${encodeURIComponent(newSearchTerm)}`,
        query: queryParams
      }).then(() => {
        setSearchTerm(newSearchTerm);
      });
    }
  };
  const handleRedirectToProduct = (productId: string): void => {
    push(`${ROUTES.products}/${encodeURIComponent(productId)}`);
  };

  useEffect(() => {
    const categoryService = new CategoryService();

    (async (): Promise<void> => {
      const selectedTenantId = JSON.parse(Cookies.get(TENANT_STORAGE_KEY) || 'null')?.id;
      const { categories: categoriesTree } =
        await categoryService.getCategoriesWithRecent(selectedTenantId);
      setCategories(categoriesTree ?? []);
    })();
  }, []);

  const initializedSearchQueryRef = useRef<string | null>(null);

  // Sync search input with URL parameter to support browser back/forward navigation.
  // Updates the search input when the URL searchQuery changes, allowing users to see the current search query.
  useEffect(() => {
    if (!isReady) return;

    const searchQuery = query.searchQuery?.toString();
    const isSearchPage = pathname.startsWith('/search/');

    if (isSearchPage && searchQuery && searchQuery !== initializedSearchQueryRef.current) {
      try {
        const decodedQuery = decodeURIComponent(searchQuery);
        setSearchTerm(decodedQuery);
        initializedSearchQueryRef.current = searchQuery;
      } catch {
        setSearchTerm(searchQuery);
        initializedSearchQueryRef.current = searchQuery;
      }
    } else if (!isSearchPage) {
      initializedSearchQueryRef.current = null;
    }
  }, [ isReady, pathname, query.searchQuery, setSearchTerm ]);

  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = headerRef.current;

    if (!el || typeof ResizeObserver === 'undefined') return;

    const syncHeight = (): void => {
      document.documentElement.style.setProperty(
        PAGE_HEADER_HEIGHT_CSS_VAR,
        `${Math.round(el.getBoundingClientRect().height)}px`
      );
    };

    syncHeight();
    const observer = new ResizeObserver(syncHeight);

    observer.observe(el);

    return (): void => {
      observer.disconnect();
      document.documentElement.style.removeProperty(PAGE_HEADER_HEIGHT_CSS_VAR);
    };
  }, []);

  return (
    <>
      <header
        ref={ headerRef }
        className={ cx(CN, { [`${CN}--sticky`]: !isPLP || isMobileOrTablet }) }
      >
        <PartnersBanner />
        <div className={ cx(toolbarClassName) }>
          <Button
            isIcon
            variant='inherit'
            onClick={ handleDrawerToggle }
            className={ cx(menuButtonClassName) }
            aria-label={ isDrawerOpen ? 'Close mobile menu' : 'Open mobile menu' }
          >
            <Icon name='mobileMenu' width={ 42 } height={ 42 } />
          </Button>
          <Link href='/' className={ cx(logoClassName) }>
            <Image src={ isMobileOrTablet ? LogoMobile.src : Logo.src } alt='Volcano logo' width={ 205 } height={ 42 } />
          </Link>
          <SearchInput
            searchEngine={ SEARCH_ENGINE }
            searchTerm={ searchTerm }
            setSearchTerm={ setSearchTerm }
            onSearch={ handleSearch }
            onRedirect={ handleRedirectToProduct }
            isConversationalSearchEnabled={ isConversationalSearchEnabled }
          />
          {isLargeScreen && (
            <TenantSelector />
          )}
          {isDesktop ? (
            <HeaderActions onCartClick={ toggleCartDrawer } />
          ) : (
            <Button
              isIcon
              variant='inherit'
              onClick={ toggleCartDrawer }
              aria-label='Open shopping cart'
              className={ cx(cartButtonClassName) }
            >
              <Icon name='shoppingBag' width={ 28 } height={ 28 } />
              {cart?.totalLineItemQuantity > 0 && (
                <span className={ cx(badgeClassName) }>
                  {cart.totalLineItemQuantity}
                </span>
              )}
            </Button>
          )}
        </div>
        <CategoriesToolbar
          categories={ categories }
          onCategoryClick={ handleCategoryClick }
        />
      </header>
      <MobileMenu
        categories={ categories }
        onCategoryClick={ handleCategoryClick }
        onClose={ handleDrawerToggle }
        isDrawerOpen={ isDrawerOpen }
        handleCartDrawerToggle={ toggleCartDrawer }
      />
      <CartDrawer
        isDrawerOpen={ isCartDrawerOpen }
        onClose={ toggleCartDrawer }
      />
    </>
  );
};

